import { scopedApi, scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { EndpointResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions, WriteRequestOptions } from './requestOptions';

type Query = Record<string, unknown>;

const safePath = (path: string): string => {
  if (!/^\/?[A-Za-z0-9_./?=&:%,$\-]+$/.test(path) || path.includes('..')) throw new Error('Invalid organization endpoint path');
  return path;
};

const readApi = async <T>(
  scope: RequestScope,
  path: string,
  params: Query | undefined,
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedApi(scope).get<EndpointResponse<T>>(safePath(path), { ...options, params });
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const getRecord = <T>(
  scope: RequestScope,
  params: { path: string; query?: Query },
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => readApi(scope, params.path, params.query, options);

export const createRecord = async <T>(
  scope: RequestScope,
  params: { path: string; data: unknown },
  options: WriteRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedApi(scope).post<EndpointResponse<T>>(safePath(params.path), params.data, options);
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const updateRecord = async <T>(
  scope: RequestScope,
  params: { path: string; data: unknown; method: 'put' | 'patch' },
  options: WriteRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const client = scopedApi(scope);
    const response =
      params.method === 'put'
        ? await client.put<EndpointResponse<T>>(safePath(params.path), params.data, options)
        : await client.patch<EndpointResponse<T>>(safePath(params.path), params.data, options);
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const deleteRecord = async <T>(scope: RequestScope, params: { path: string }, options: WriteRequestOptions): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedApi(scope).delete<EndpointResponse<T>>(safePath(params.path), options);
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const getSharedData = (
  scope: RequestScope,
  params: { path: string; query?: Query },
  options: ReadRequestOptions
): Promise<EndpointResponse<unknown>> => readApi(scope, params.path, params.query, options);

export const getChannelSharedData = async (
  scope: RequestScope,
  params: { path: string; query?: Query },
  options: ReadRequestOptions
): Promise<EndpointResponse<unknown>> => {
  try {
    const response = await scopedChannelApi(scope).get<unknown>(safePath(params.path), { ...options, params: params.query });
    return { result: response.data, status: response.status, execution_time: 0, num_results: 1 };
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const getCatalogItemImages = (
  scope: RequestScope,
  params: { productIds: Array<string | number> },
  options: ReadRequestOptions
): Promise<EndpointResponse<Array<{ id: string | number; images?: { default?: Array<{ url?: string }> } }>>> =>
  readApi(scope, '/catalog', { query: JSON.stringify({ id: { $in: params.productIds } }) }, options);

export const getInventoryRecords = (scope: RequestScope, params: { query: unknown }, options: ReadRequestOptions): Promise<EndpointResponse<any[]>> =>
  readApi(scope, '/inventory', { query: JSON.stringify(params.query) }, options);

export const getChannelItemMappings = (
  scope: RequestScope,
  params: { productId: string | number },
  options: ReadRequestOptions
): Promise<EndpointResponse<any[]>> =>
  readApi(scope, '/channel_item_mapping', { limit: 100, query: JSON.stringify({ product_id: params.productId }) }, options);

/** What image_srv processes: a base64 payload, or a URL it fetches the image from. */
export type ImageUploadSource = Readonly<{ data: string }> | Readonly<{ src: string }>;

/** The result is image_srv's record of the stored image; callers check its fields before using them. */
export const uploadImage = async (
  scope: RequestScope,
  params: ImageUploadSource,
  options: WriteRequestOptions
): Promise<EndpointResponse<unknown>> => {
  try {
    // image_srv's ImageUpload reads only data or src (plus alt), so nothing else is sent: no filename, which it has no
    // field for, and no list-query keys like `attributes`.
    const response = await scopedApi(scope).post<EndpointResponse<unknown>>('/image', params, options);
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

/**
 * `T` is the record the write returns, which isn't the full record: kitchensink loads only direct associations for it
 * (not a variant's pricing), sends a catalog item's `group_ids` and `group_skus` on GET alone, and leaves out any value
 * the write emptied.
 */
export const updateEditableRecord = async <T = Record<string, unknown>>(
  scope: RequestScope,
  params: { modelName: string; id: string | number; data: unknown; method: 'put' | 'patch' },
  options: WriteRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const client = scopedApi(scope);
    const path = `/${safePath(params.modelName)}/${encodeURIComponent(String(params.id))}`;
    const response =
      params.method === 'put'
        ? await client.put<EndpointResponse<T>>(path, params.data, options)
        : await client.patch<EndpointResponse<T>>(path, params.data, options);
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

const postWrite = async <T>(scope: RequestScope, path: string, data: unknown, options: WriteRequestOptions): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedApi(scope).post<EndpointResponse<T>>(safePath(path), data, options);
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const retryOrder = <T>(scope: RequestScope, params: { orderId: string | number }, options: WriteRequestOptions) =>
  postWrite<T>(scope, `/order/retry/${encodeURIComponent(String(params.orderId))}`, {}, options);

export const rerouteShipAdviceItem = <T>(scope: RequestScope, params: { data: unknown }, options: WriteRequestOptions) =>
  postWrite<T>(scope, '/ship_advice_item_reroute', params.data, options);

export const inviteOrganizationUser = <T>(scope: RequestScope, params: { data: unknown }, options: WriteRequestOptions) =>
  postWrite<T>(scope, '/organization/invite', params.data, options);

export const updateDeliveryInformation = async <T>(
  scope: RequestScope,
  params: { id: string | number; data: unknown },
  options: WriteRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedApi(scope).patch<EndpointResponse<T>>(
      `/delivery_information/${encodeURIComponent(String(params.id))}`,
      params.data,
      options
    );
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const updateOrganizationUser = async <T>(
  scope: RequestScope,
  params: { id: string | number; data: unknown },
  options: WriteRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedApi(scope).put<EndpointResponse<T>>(
      `/organization/user/${encodeURIComponent(String(params.id))}`,
      params.data,
      options
    );
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const getPostingTemplateMetadata = async <T>(
  scope: RequestScope,
  params: { postingTemplateId: string | number; subscriptionId?: string | number },
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedChannelApi(scope).get<T>(`atts/posting_template/${encodeURIComponent(String(params.postingTemplateId))}`, {
      ...options,
      headers: params.subscriptionId === undefined ? undefined : { 'x-astur-subscription-id': String(params.subscriptionId) },
    });
    return { result: response.data, status: response.status, execution_time: 0, num_results: 1 };
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const searchSuggestedFixes = async <T>(
  scope: RequestScope,
  params: { queryString: string; subscriptionId?: string | number },
  options: ReadRequestOptions
): Promise<EndpointResponse<T>> => {
  try {
    const response = await scopedChannelApi(scope).get<T>(`/astur/integrations/channel/errors/search?${params.queryString}`, {
      ...options,
      headers: params.subscriptionId === undefined ? undefined : { 'X-astur-subscription-id': String(params.subscriptionId) },
    });
    return { result: response.data, status: response.status, execution_time: 0, num_results: 1 };
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const exportSuggestedFixes = (
  scope: RequestScope,
  params: { queryString: string; subscriptionId?: string | number },
  options: ReadRequestOptions
) => searchSuggestedFixes<string>(scope, params, options);

export const getSuggestedFixCount = async (
  scope: RequestScope,
  params: { subscriptionId: string | number },
  options: ReadRequestOptions
): Promise<EndpointResponse<unknown>> => {
  try {
    const subscriptionId = String(params.subscriptionId);
    const response = await scopedChannelApi(scope).get<unknown>(`/astur/integrations/channel/errors/count/${encodeURIComponent(subscriptionId)}`, {
      ...options,
      headers: { 'X-astur-subscription-id': subscriptionId },
    });
    return { result: response.data, status: response.status, execution_time: 0, num_results: 1 };
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};
